import type { PropsWithChildren } from 'react';
import * as React from 'react';
import {
  D2N_SUB_ID,
  G2T_SUB_ID,
  P2N_SUB_ID,
  S2N_SUB_ID,
  SC2N_SUB_ID,
  KTS_SUB_ID,
  V2N_SUB_ID,
  W2N_SUB_ID,
} from '../../constants';
import { JobType } from '../../entities';
import type { AppContextProps } from './AppConstantsContext';
import AppConstantsContext, { initConstantValues } from './AppConstantsContext';
import { MUSE_ID_KEY } from '../User/UserProvider';
import getEnvMode from '../../utils/env';

const P2N_CONSTANTS: Partial<AppContextProps> = {
  title: 'Piano2Notes',
  gaTrackingId: '',
  gaTrackingId_prod: 'G-R4N8J9W4GC',
  gaTrackingId_dev: 'G-24BC4TNPM9',
  crossDomains: '',
  awConversionId: 'AW-739708467/n9glCJ6rldsCELOc3OAC',
  languages: ['en', 'de', 'es', 'fr', 'it', 'ko', 'pt', 'jp', 'ru', 'uk'],
  supportMail: 'support@piano2notes.com',
  supportSubject: '[P2N] Support request',
  singleProductId: 650761,
  subscriptionIds: P2N_SUB_ID,
  iosUrl: 'https://apps.apple.com/app/apple-store/id1589420929',
  androidUrl:
    'https://play.google.com/store/apps/details?id=com.piano2notes&utm_source=piano2notes.com&utm_campaign=piano2notes.com&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1',
  url: 'https://piano2notes.klang.io',
  wpLandingPage: 'https://klang.io/piano2notes',
  inviteFriends: 'https://piano2notes.onelink.me/4csv/zxbtvec0?user_id=',
  helpLink: 'https://klang.io/help/p2n/',
  jobType: JobType.TranscribePiano,
  musehubPurchaseUrl:
    'musehub://product/store?id=5977a39f-86f0-4ac0-9a76-835e799eb2aa&type=application',
};

const G2T_CONSTANTS: Partial<AppContextProps> = {
  title: 'Guitar2Tabs',
  gaTrackingId: '',
  gaTrackingId_prod: 'G-FX73VZZ4CC',
  gaTrackingId_dev: 'G-MF31Q7YCKR',
  crossDomains: '',
  awConversionId: 'AW-739708467/n9glCJ6rldsCELOc3OAC',
  languages: ['en', 'de', 'es', 'fr', 'it', 'ko', 'pt', 'jp', 'ru', 'uk'],
  supportMail: 'support@guitar2tabs.com',
  supportSubject: '[G2T] Support request',
  singleProductId: 674726,
  subscriptionIds: G2T_SUB_ID,
  iosUrl: 'https://apps.apple.com/app/apple-store/id1589420929',
  androidUrl: 'https://play.google.com/store/apps/details?id=com.klangio.guitar2tabs',
  url: 'https://guitar2tabs.klang.io',
  wpLandingPage: 'https://klang.io/guitar2tabs',
  inviteFriends: 'https://guitar2tabs.onelink.me/Ysge/iskgebc6?user_id=',
  helpLink: 'https://klang.io/help/g2t/',
  jobType: JobType.TranscribeGuitar,
  musehubPurchaseUrl:
    'musehub://product/store?id=68d0bf45-2b07-439a-a0c1-5a6186a82010&type=application',
};

const S2N_CONSTANTS: Partial<AppContextProps> = {
  title: 'Sing2Notes',
  gaTrackingId: '',
  gaTrackingId_prod: 'G-4RR7ZCMEJ8',
  gaTrackingId_dev: 'UA-67983997-11',
  crossDomains: '',
  awConversionId: 'AW-739708467/n9glCJ6rldsCELOc3OAC',
  languages: ['en', 'de', 'es', 'fr', 'it', 'ko', 'pt', 'jp', 'ru', 'uk'],
  supportMail: 'support@sing2notes.com',
  supportSubject: '[S2N] Support request',
  singleProductId: parseInt(S2N_SUB_ID.SINGLE, 10),
  subscriptionIds: S2N_SUB_ID,
  iosUrl: 'https://apps.apple.com/us/app/sing2notes/id1588721118',
  androidUrl: 'https://play.google.com/store/apps/details?id=com.sing2notes',
  url: 'https://sing2notes.klang.io',
  wpLandingPage: 'https://klang.io/sing2notes',
  inviteFriends: 'https://sing2notes.onelink.me/tKGN/dqgttgp3?user_id=',
  helpLink: 'https://klang.io/help/s2n/',
  jobType: JobType.TranscribeSinging,
};

// TODO: set D2N_CONSTANTS! - values are from sing2notes
const D2N_CONSTANTS: Partial<AppContextProps> = {
  title: 'Drum2Notes',
  gaTrackingId: '',
  gaTrackingId_prod: 'G-4RR7ZCMEJ8',
  gaTrackingId_dev: 'UA-67983997-11',
  crossDomains: '',
  awConversionId: 'AW-739708467/n9glCJ6rldsCELOc3OAC',
  languages: ['en', 'de', 'es', 'fr', 'it', 'ko', 'pt', 'jp', 'ru', 'uk'],
  supportMail: 'support@drum2notes.com',
  supportSubject: '[D2N] Support request',
  singleProductId: parseInt(D2N_SUB_ID.SINGLE, 10),
  subscriptionIds: D2N_SUB_ID,
  url: 'https://drum2notes.klang.io',
  wpLandingPage: 'https://klang.io/drum2notes',
  inviteFriends: 'https://klang.io/drum2notes?user_id=',
  helpLink: 'https://klang.io/help',
  jobType: JobType.TranscribeDrums,
};

const STUDIO_CONSTANTS: Partial<AppContextProps> = {
  title: 'Klangio Transcription Studio',
  gaTrackingId: '',
  gaTrackingId_prod: 'G-4RR7ZCMEJ8',
  gaTrackingId_dev: 'UA-67983997-11',
  crossDomains: '',
  awConversionId: 'AW-739708467/n9glCJ6rldsCELOc3OAC',
  languages: ['en', 'de', 'es', 'fr', 'it', 'ko', 'pt', 'jp', 'ru', 'uk'],
  supportMail: 'support@klangio.com',
  supportSubject: '[KTS] Support request',
  singleProductId: parseInt(KTS_SUB_ID.SINGLE, 10),
  subscriptionIds: KTS_SUB_ID,
  url: 'https://studio.klang.io',
  wpLandingPage: 'https://klang.io/transcription-studio',
  inviteFriends: 'https://klang.io/transcription-studio?user_id=', // TODO: add invitation link
  helpLink: 'https://klang.io/help',
  jobType: JobType.TranscribeStudio,
};
const SCAN_2_NOTES: Partial<AppContextProps> = {
  title: 'Scan2Notes',
  gaTrackingId: '',
  gaTrackingId_prod: 'G-4RR7ZCMEJ8',
  gaTrackingId_dev: 'UA-67983997-11',
  crossDomains: '',
  awConversionId: 'AW-739708467/n9glCJ6rldsCELOc3OAC',
  languages: ['en', 'de', 'es', 'fr', 'it', 'ko', 'pt', 'jp', 'ru', 'uk'],
  supportMail: 'support@klangio.com',
  supportSubject: '[KTS] Support request',
  singleProductId: parseInt(SC2N_SUB_ID.SINGLE, 10),
  subscriptionIds: SC2N_SUB_ID,
  url: 'https://scan2notes.klang.io',
  wpLandingPage: 'https://klang.io/scan2notes',
  inviteFriends: 'https://klang.io/scan2notes?user_id=', // TODO: add invitation link
  helpLink: 'https://klang.io/help',
  jobType: JobType.TranscribeScan,
};
const W2N_CONSTANTS: Partial<AppContextProps> = {
  title: 'Wind2Notes',
  gaTrackingId: '',
  gaTrackingId_prod: 'G-4RR7ZCMEJ8',
  gaTrackingId_dev: 'UA-67983997-11',
  crossDomains: '',
  awConversionId: 'AW-739708467/n9glCJ6rldsCELOc3OAC',
  languages: ['en', 'de', 'es', 'fr', 'it', 'ko', 'pt', 'jp', 'ru', 'uk'],
  supportMail: 'support@klangio.com',
  supportSubject: '[KTS] Support request',
  singleProductId: parseInt(W2N_SUB_ID.SINGLE, 10),
  subscriptionIds: W2N_SUB_ID,
  url: 'https://wind2notes.klang.io',
  wpLandingPage: 'https://klang.io/wind2notes',
  inviteFriends: 'https://klang.io/wind2notes?user_id=', // TODO: add invitation link
  helpLink: 'https://klang.io/help',
  jobType: JobType.TranscribeWind,
};
const V2N_CONSTANTS: Partial<AppContextProps> = {
  title: 'Violin2Notes',
  gaTrackingId: '',
  gaTrackingId_prod: 'G-4RR7ZCMEJ8',
  gaTrackingId_dev: 'UA-67983997-11',
  crossDomains: '',
  awConversionId: 'AW-739708467/n9glCJ6rldsCELOc3OAC',
  languages: ['en', 'de', 'es', 'fr', 'it', 'ko', 'pt', 'jp', 'ru', 'uk'],
  supportMail: 'support@klangio.com',
  supportSubject: '[KTS] Support request',
  singleProductId: parseInt(V2N_SUB_ID.SINGLE, 10),
  subscriptionIds: V2N_SUB_ID,
  url: 'https://violin2notes.klang.io',
  wpLandingPage: 'https://klang.io/violin2notes',
  inviteFriends: 'https://klang.io/violin2notes?user_id=', // TODO: add invitation link
  helpLink: 'https://klang.io/help',
  jobType: JobType.TranscribeViolin,
};
export interface AppConstantsProviderProps extends PropsWithChildren {
  jobType: JobType;
}

const AppConstantsProvider: React.FC<AppConstantsProviderProps> = ({ children, jobType }) => {
  let constants = initConstantValues;
  const isMusehub =
    typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).get(MUSE_ID_KEY) !== null ||
      localStorage.getItem(MUSE_ID_KEY) !== null);

  if (jobType === JobType.TranscribePiano) {
    constants = {
      ...constants,
      ...P2N_CONSTANTS,
      gaTrackingId:
        getEnvMode() === 'production'
          ? P2N_CONSTANTS.gaTrackingId_prod
          : P2N_CONSTANTS.gaTrackingId_dev,
      isMusehub,
    };
  } else if (jobType === JobType.TranscribeGuitar) {
    constants = {
      ...constants,
      ...G2T_CONSTANTS,
      gaTrackingId:
        getEnvMode() === 'production'
          ? G2T_CONSTANTS.gaTrackingId_prod
          : G2T_CONSTANTS.gaTrackingId_dev,
      isMusehub,
    };
  } else if (jobType === JobType.TranscribeSinging) {
    constants = {
      ...constants,
      ...S2N_CONSTANTS,
      gaTrackingId:
        getEnvMode() === 'production'
          ? S2N_CONSTANTS.gaTrackingId_prod
          : S2N_CONSTANTS.gaTrackingId_dev,
      isMusehub,
    };
  } else if (jobType === JobType.TranscribeDrums) {
    constants = {
      ...constants,
      ...D2N_CONSTANTS,
      gaTrackingId:
        getEnvMode() === 'production'
          ? D2N_CONSTANTS.gaTrackingId_prod
          : D2N_CONSTANTS.gaTrackingId_dev,
      isMusehub,
    };
  } else if (jobType === JobType.TranscribeStudio) {
    constants = {
      ...constants,
      ...STUDIO_CONSTANTS,
      gaTrackingId:
        getEnvMode() === 'production'
          ? STUDIO_CONSTANTS.gaTrackingId_prod
          : STUDIO_CONSTANTS.gaTrackingId_dev,
      isMusehub,
    };
  } else if (jobType === JobType.TranscribeScan) {
    constants = {
      ...constants,
      ...SCAN_2_NOTES,
      gaTrackingId:
        getEnvMode() === 'production'
          ? SCAN_2_NOTES.gaTrackingId_prod
          : SCAN_2_NOTES.gaTrackingId_dev,
      isMusehub,
    };
  } else if (jobType === JobType.TranscribeWind) {
    constants = {
      ...constants,
      ...W2N_CONSTANTS,
      gaTrackingId:
        getEnvMode() === 'production'
          ? W2N_CONSTANTS.gaTrackingId_prod
          : W2N_CONSTANTS.gaTrackingId_dev,
      isMusehub,
    };
  } else if (jobType === JobType.TranscribeViolin) {
    constants = {
      ...constants,
      ...V2N_CONSTANTS,
      gaTrackingId:
        getEnvMode() === 'production'
          ? V2N_CONSTANTS.gaTrackingId_prod
          : V2N_CONSTANTS.gaTrackingId_dev,
      isMusehub,
    };
  } else {
    throw new Error(`JobType ${jobType} not supported!`);
  }

  return <AppConstantsContext.Provider value={constants}>{children}</AppConstantsContext.Provider>;
};

export default AppConstantsProvider;
